import axios, { type AxiosResponse } from 'axios';

/**
 * Correlation between a UI action and the server log. The API stamps every response with
 * `X-Trace-Id` (the 32-hex W3C trace id of the request), and error ProblemDetails carry a
 * `traceId` that may be the full W3C `traceparent` form (`00-<32>-<16>-<2>`).
 */
export const TRACE_ID_HEADER = 'x-trace-id';

const HEX32 = /^[0-9a-f]{32}$/i;
const W3C = /^[0-9a-f]{2}-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/i;

/** 32-hex lowercase trace id, or undefined when `raw` is neither a 32-hex id nor a W3C traceparent. */
export function normalizeTraceId(raw?: string | null): string | undefined {
  const value = raw?.trim();
  if (!value) return undefined;
  if (HEX32.test(value)) return value.toLowerCase();
  const w3c = W3C.exec(value);
  return w3c ? w3c[1].toLowerCase() : undefined;
}

function headerValue(headers: unknown): string | undefined {
  if (!headers || typeof headers !== 'object') return undefined;
  // AxiosHeaders exposes get(); a plain object (tests, adapters) is indexed directly.
  const h = headers as { get?: (name: string) => unknown } & Record<string, unknown>;
  const value = typeof h.get === 'function' ? h.get(TRACE_ID_HEADER) : h[TRACE_ID_HEADER];
  return typeof value === 'string' ? value : undefined;
}

/** The trace id the API stamped on a successful response. */
export function traceIdFromResponse(res: Pick<AxiosResponse, 'headers'>): string | undefined {
  return normalizeTraceId(headerValue(res.headers));
}

/** The trace id of a failed request: the response header first, then ProblemDetails `traceId`. */
export function traceIdFromError(err: unknown): string | undefined {
  if (!axios.isAxiosError(err) || !err.response) return undefined;
  const fromHeader = normalizeTraceId(headerValue(err.response.headers));
  if (fromHeader) return fromHeader;
  const data: unknown = err.response.data;
  if (data && typeof data === 'object' && 'traceId' in data) {
    const traceId = (data as { traceId?: unknown }).traceId;
    return typeof traceId === 'string' ? normalizeTraceId(traceId) : undefined;
  }
  return undefined;
}
